// Reusable Swiper setup for every sonible carousel.
//
// HOW IT WORKS
// Unlike the clever-fit sliders - which each have their own JS file with a
// hardcoded selector and hardcoded Swiper options - there is exactly one
// initialiser here. It scans for [data-son-carousel] and configures each
// instance from its own data attributes, so a new carousel widget needs no
// JS at all: it just prints the attributes and gets picked up.
//
// MARKUP CONTRACT
//   <div class="… swiper" data-son-carousel data-son-carousel-gap="20">
//     <div class="swiper-wrapper">
//       <div class="swiper-slide">…</div>
//     </div>
//     <div class="son-carousel__fade son-carousel__fade--start"></div>
//     <div class="son-carousel__fade son-carousel__fade--end"></div>
//     <button class="son-carousel__prev">‹</button>
//     <button class="son-carousel__next">›</button>
//   </div>
//
// OPTIONS (all optional, read off the container)
//   data-son-carousel-gap="20"        -> spaceBetween in px (default: --son-carousel-gap
//                                        from the son-carousel mixin, the shared grid gap)
//   data-son-carousel-per-view="auto" -> slidesPerView
//   data-son-carousel-enabled="false" -> carousel off at the base (smallest) width
//   data-son-carousel-breakpoints='{"1025":{"enabled":false}}'
//                                     -> raw Swiper breakpoints, min-width keyed.
//                                        Used for the responsive grid/carousel
//                                        switch: where enabled is false the
//                                        carousel stands down and the wrapper
//                                        renders as a plain wrapping grid.
//
// WHAT SWIPER HANDLES vs WHAT WE HANDLE
//   Buttons: Swiper adds .swiper-button-disabled at the edges and
//     .swiper-button-lock when the track cannot move at all. The SCSS hides
//     both, so the chevrons appear and disappear on their own.
//   Fades: Swiper adds no class for the container edge state, so syncEdges()
//     below toggles .is-at-start / .is-at-end and the SCSS fades accordingly.
//
// The dataset.sonCarouselReady flag guards against double initialisation:
// Elementor can fire element_ready for the same element more than once in
// the editor, which would otherwise attach two Swiper instances to one node.

const { default: Swiper } = require('swiper');
const { Navigation, A11y } = require('swiper/modules');

const SELECTOR = '[data-son-carousel]';

function initCarousel(container) {
  if (!container || container.dataset.sonCarouselReady) {
    return;
  }

  const wrapper = container.querySelector('.swiper-wrapper');

  if (!wrapper || !wrapper.children.length) {
    return;
  }

  container.dataset.sonCarouselReady = 'true';

  let breakpoints;

  try {
    breakpoints = JSON.parse(container.dataset.sonCarouselBreakpoints || 'null') || undefined;
  } catch (e) {
    breakpoints = undefined;
  }

  // Swiper handles the buttons via .swiper-button-disabled, but adds no class
  // for the container edge state or for being disabled, so both are mirrored
  // onto the container here for the SCSS to hook.
  const syncState = (swiper) => {
    container.classList.toggle('is-at-start', swiper.isBeginning);
    container.classList.toggle('is-at-end', swiper.isEnd);
    container.classList.toggle('is-grid', !swiper.enabled);
  };

  new Swiper(container, {
    modules: [Navigation, A11y],
    slidesPerView: container.dataset.sonCarouselPerView || 'auto',
    spaceBetween: parseInt(container.dataset.sonCarouselGap, 10)
      || parseInt(getComputedStyle(container).getPropertyValue('--son-carousel-gap'), 10)
      || 20,
    enabled: container.dataset.sonCarouselEnabled !== 'false',
    breakpoints,
    watchOverflow: true,
    navigation: {
      nextEl: container.querySelector('.son-carousel__next'),
      prevEl: container.querySelector('.son-carousel__prev'),
    },
    a11y: { enabled: true },
    on: {
      init: syncState,
      slideChange: syncState,
      resize: syncState,
      breakpoint: syncState,
    },
  });
}

function initAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach(initCarousel);
}

document.addEventListener('DOMContentLoaded', () => initAll());

// Elementor re-renders widgets in the editor without a page reload.
window.addEventListener('elementor/frontend/init', () => {
  if (!window.elementorFrontend) {
    return;
  }

  elementorFrontend.hooks.addAction('frontend/element_ready/global', ($scope) => {
    initAll($scope[0] || document);
  });
});

module.exports = { initCarousel, initAll };
